import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { __, sprintf } from '@wordpress/i18n'
import SbUtils from '../../Utils/SbUtils'
import Checkbox from './Checkbox'
import SafeHtml from './SafeHtml'
import { TagIcon, FolderIcon, HighlightMouseCursorIcon, LinkOffIcon, CloseIcon } from './Icons/WooIcons'
import useDebounce from '../../Utils/useDebounce'

// Tab types
const TAB_DOWNLOADS = 'downloads';
const TAB_CATEGORIES = 'categories';
const TAB_TAGS = 'tags';

/**
 * Shared EDD Download Selector Component
 *
 * Used by both AddSourceModal and EditEddDownloadsModal for consistent UI.
 * Supports three tabs: Downloads, Categories, Tags.
 *
 * @param {Object} props
 * @param {Array} props.downloads - All available downloads
 * @param {Array} props.selectedDownloads - Currently selected downloads
 * @param {Function} props.onDownloadToggle - Called when a download is selected/deselected
 * @param {Function} props.onSelectAll - Called when Select All is clicked (receives filtered downloads)
 * @param {Function} props.onDeselectAll - Called when Deselect All is clicked
 * @param {boolean} props.isLoading - Whether downloads are loading
 * @param {string|null} props.validationError - Validation error message
 * @param {boolean} props.showTabs - Whether to show the tabs (default: false for backward compatibility)
 * @param {Array} props.categories - All available categories (for tabs mode)
 * @param {Array} props.selectedCategories - Currently selected categories
 * @param {Function} props.onCategoryToggle - Called when a category is selected/deselected
 * @param {boolean} props.loadingCategories - Whether categories are loading
 * @param {Array} props.tags - All available tags (for tabs mode)
 * @param {Array} props.selectedTags - Currently selected tags
 * @param {Function} props.onTagToggle - Called when a tag is selected/deselected
 * @param {boolean} props.loadingTags - Whether tags are loading
 * @param {Function} props.onClearAll - Called when Clear All is clicked (clears all selection types)
 * @param {Function} props.onSearchDownloads - Called when search query changes (for server-side search)
 * @param {boolean} props.isSearching - Whether a server-side search is in progress (shows skeleton loaders)
 * @param {Function} props.onSelectAllCategories - Called when Select All categories is clicked
 * @param {Function} props.onDeselectAllCategories - Called when Deselect All categories is clicked
 * @param {Function} props.onSelectAllTags - Called when Select All tags is clicked
 * @param {Function} props.onDeselectAllTags - Called when Deselect All tags is clicked
 */
const EddDownloadSelector = ( props ) => {
    const {
        downloads = [],
        selectedDownloads = [],
        onDownloadToggle,
        onSelectAll,
        onDeselectAll,
        isLoading = false,
        validationError = null,
        // New props for tabs mode
        showTabs = false,
        categories = [],
        selectedCategories = [],
        onCategoryToggle,
        loadingCategories = false,
        tags = [],
        selectedTags = [],
        onTagToggle,
        loadingTags = false,
        onClearAll,
        // Server-side search props
        onSearchDownloads,
        isSearching = false,
        // Batch select/deselect for categories and tags
        onSelectAllCategories,
        onDeselectAllCategories,
        onSelectAllTags,
        onDeselectAllTags
    } = props;

    const [ searchQuery, setSearchQuery ] = useState( '' );
    const [ activeTab, setActiveTab ] = useState( TAB_DOWNLOADS );
    // Track what query was last sent to the server (prevents flash when debounce fires)
    const [ lastSearchedQuery, setLastSearchedQuery ] = useState( '' );

    // Track if this is the initial mount (to skip empty search on mount)
    const isInitialMount = useRef( true );
    // Track the previous search query to detect actual changes
    const previousSearchQuery = useRef( '' );

    // Debounce search query for server-side search (1 second delay)
    const debouncedSearchQuery = useDebounce( searchQuery, 1000 );

    // Reset search tracking when switching tabs to prevent stale state
    useEffect( () => {
        setLastSearchedQuery( '' );
        previousSearchQuery.current = '';
    }, [ activeTab ] );

    // Detect if a search is pending or needs to be triggered
    // This prevents content flash between debounce firing and search results arriving
    const isSearchPending = useMemo( () => {
        // Only for Downloads tab with server-side search
        if ( activeTab !== TAB_DOWNLOADS || ! onSearchDownloads ) {
            return false;
        }
        // Pending if:
        // 1. User is still typing (searchQuery !== debouncedSearchQuery)
        // 2. OR debounce fired but search hasn't been triggered yet (debouncedSearchQuery !== lastSearchedQuery)
        return searchQuery !== debouncedSearchQuery || debouncedSearchQuery !== lastSearchedQuery;
    }, [ searchQuery, debouncedSearchQuery, lastSearchedQuery, activeTab, onSearchDownloads ] );

    // Trigger server-side search when debounced query changes (Downloads tab only)
    useEffect( () => {
        // Skip initial mount - downloads are already loaded by parent
        if ( isInitialMount.current ) {
            isInitialMount.current = false;
            return;
        }

        // Only trigger server search for Downloads tab, non-URL searches, and when callback exists
        if ( ! onSearchDownloads || activeTab !== TAB_DOWNLOADS ) {
            return;
        }

        // Skip if query hasn't actually changed
        if ( debouncedSearchQuery === previousSearchQuery.current ) {
            return;
        }
        previousSearchQuery.current = debouncedSearchQuery;

        // Check if it looks like a URL (handled client-side)
        // Only detect explicit URLs with protocols or WordPress admin paths
        // Avoid false positives like product names containing ".com"
        const query = debouncedSearchQuery.trim().toLowerCase();
        const looksLikeUrl = /^(https?|ftp):\/\/[^\s]+$/i.test( query ) ||
            query.includes( '/wp-admin/' ) ||
            /^localhost(:\d+)?/.test( query );

        if ( looksLikeUrl ) {
            // Update lastSearchedQuery to resolve isSearchPending state
            // URL searches are handled client-side, but we need to mark query as "searched"
            setLastSearchedQuery( debouncedSearchQuery );
            return;
        }

        // Trigger server search for queries with 2+ characters or empty (to reset)
        if ( debouncedSearchQuery.length >= 2 || debouncedSearchQuery === '' ) {
            setLastSearchedQuery( debouncedSearchQuery );
            onSearchDownloads( debouncedSearchQuery );
        } else {
            // For single-character queries, update lastSearchedQuery to resolve pending state
            // This prevents infinite loading when user types just 1 character
            setLastSearchedQuery( debouncedSearchQuery );
        }
    }, [ debouncedSearchQuery, activeTab, onSearchDownloads ] );

    /**
     * Parse a download URL to extract download ID or slug
     * Handles:
     * 1. Admin edit URL: /wp-admin/post.php?post=111&action=edit
     * 2. Pretty permalink: /downloads/download-slug/
     * 3. Default permalink: ?p=111 or ?download=111
     *
     * @param {string} url - The URL to parse
     * @returns {Object|null} - { type: 'id'|'slug', value: string|number } or null
     */
    const parseDownloadUrl = useCallback( ( url ) => {
        if ( !url ) return null;

        const trimmedUrl = url.trim();

        try {
            // Try to parse as URL
            let urlObj;
            try {
                urlObj = new URL( trimmedUrl );
            } catch {
                // If it's not a valid URL, it might be a partial URL or just text
                return null;
            }

            const pathname = urlObj.pathname;
            const searchParams = urlObj.searchParams;

            // Case 1: Admin edit URL - /wp-admin/post.php?post=111&action=edit
            if ( pathname.includes( '/wp-admin/post.php' ) ) {
                const postId = searchParams.get( 'post' );
                if ( postId && !isNaN( parseInt( postId, 10 ) ) ) {
                    return { type: 'id', value: parseInt( postId, 10 ) };
                }
            }

            // Case 2: Default permalink - ?p=111
            const pParam = searchParams.get( 'p' );
            if ( pParam && !isNaN( parseInt( pParam, 10 ) ) ) {
                return { type: 'id', value: parseInt( pParam, 10 ) };
            }

            // Case 3: Download in query - ?download=111
            const downloadParam = searchParams.get( 'download' );
            if ( downloadParam && !isNaN( parseInt( downloadParam, 10 ) ) ) {
                return { type: 'id', value: parseInt( downloadParam, 10 ) };
            }

            // Case 4: Pretty permalink - /downloads/download-slug/ or just /download-slug/
            // Remove trailing slash and get path segments
            const pathSegments = pathname.replace( /^\/|\/$/g, '' ).split( '/' );

            if ( pathSegments.length > 0 ) {
                // Check if it's a /downloads/slug pattern
                const downloadIndex = pathSegments.indexOf( 'downloads' );
                if ( downloadIndex !== -1 && pathSegments.length > downloadIndex + 1 ) {
                    const slug = pathSegments[ downloadIndex + 1 ];
                    // Clean up slug (remove file extensions)
                    const cleanSlug = slug.replace( /\.(html|htm|php)$/, '' );
                    if ( cleanSlug ) {
                        return { type: 'slug', value: cleanSlug };
                    }
                }

                // Fallback: use the last segment as slug
                const lastSegment = pathSegments[ pathSegments.length - 1 ];
                const cleanSlug = lastSegment.replace( /\.(html|htm|php)$/, '' );
                // Only use if it's not a common path like 'shop', 'cart', 'checkout'
                const excludedPaths = [ 'shop', 'cart', 'checkout', 'my-account', 'wp-admin', 'wp-content', 'wp-includes' ];
                if ( cleanSlug && !excludedPaths.includes( cleanSlug.toLowerCase() ) ) {
                    return { type: 'slug', value: cleanSlug };
                }
            }

        } catch ( e ) {
            // URL parsing failed, return null
            return null;
        }

        return null;
    }, [] );

    // Check if search query looks like a URL
    // Must match the server-side looksLikeUrl logic to avoid mismatch
    const isUrlSearch = useMemo( () => {
        if ( !searchQuery ) return false;
        const query = searchQuery.trim().toLowerCase();
        // Only detect explicit URLs with protocols or WordPress admin paths
        // Avoid false positives like product names containing ".com"
        return /^(https?|ftp):\/\/[^\s]+$/i.test( query ) ||
            query.includes( '/wp-admin/' ) ||
            /^localhost(:\d+)?/.test( query );
    }, [ searchQuery ] );

    // Parse URL search to get download identifier
    const urlSearchResult = useMemo( () => {
        if ( !isUrlSearch ) return null;
        return parseDownloadUrl( searchQuery );
    }, [ isUrlSearch, searchQuery, parseDownloadUrl ] );

    // Memoize selected download IDs for faster lookup
    const selectedDownloadIds = useMemo( () => {
        return new Set( selectedDownloads.map( d => d.id ) );
    }, [ selectedDownloads ] );

    const selectedCategoryIds = useMemo( () => {
        return new Set( selectedCategories.map( c => c.id ) );
    }, [ selectedCategories ] );

    const selectedTagIds = useMemo( () => {
        return new Set( selectedTags.map( t => t.id ) );
    }, [ selectedTags ] );

    const isDownloadSelected = useCallback( ( downloadId ) => {
        return selectedDownloadIds.has( downloadId );
    }, [ selectedDownloadIds ] );

    const isCategorySelected = useCallback( ( categoryId ) => {
        return selectedCategoryIds.has( categoryId );
    }, [ selectedCategoryIds ] );

    const isTagSelected = useCallback( ( tagId ) => {
        return selectedTagIds.has( tagId );
    }, [ selectedTagIds ] );

    const filteredDownloads = useMemo( () => {
        // URL search is handled client-side (filter by parsed ID or slug)
        if ( isUrlSearch && urlSearchResult ) {
            return downloads.filter( download => {
                if ( urlSearchResult.type === 'id' ) {
                    return download.id === urlSearchResult.value;
                } else if ( urlSearchResult.type === 'slug' ) {
                    // Match by exact slug or partial slug match
                    return download.slug === urlSearchResult.value ||
                           ( download.slug && download.slug.toLowerCase() === urlSearchResult.value.toLowerCase() );
                }
                return false;
            });
        }

        // If it looks like a URL but couldn't be parsed, return empty
        // This shows the "no downloads found with that URL" message
        if ( isUrlSearch ) {
            return [];
        }

        // For text search, server-side search handles filtering
        // Just return the downloads from props (already filtered by server)
        return downloads;
    }, [ downloads, isUrlSearch, urlSearchResult ] );

    const filteredCategories = useMemo( () => {
        return categories.filter( category => {
            if ( !searchQuery ) return true;
            const query = searchQuery.toLowerCase();
            return category.name.toLowerCase().includes( query );
        });
    }, [ categories, searchQuery ] );

    const filteredTags = useMemo( () => {
        return tags.filter( tag => {
            if ( !searchQuery ) return true;
            const query = searchQuery.toLowerCase();
            return tag.name.toLowerCase().includes( query );
        });
    }, [ tags, searchQuery ] );

    // Check if all filtered downloads are selected
    const allFilteredDownloadsSelected = useMemo( () => {
        return filteredDownloads.length > 0 && filteredDownloads.every( d => selectedDownloadIds.has( d.id ) );
    }, [ filteredDownloads, selectedDownloadIds ] );

    // Check if all filtered categories are selected
    const allFilteredCategoriesSelected = useMemo( () => {
        return filteredCategories.length > 0 && filteredCategories.every( c => selectedCategoryIds.has( c.id ) );
    }, [ filteredCategories, selectedCategoryIds ] );

    // Check if all filtered tags are selected
    const allFilteredTagsSelected = useMemo( () => {
        return filteredTags.length > 0 && filteredTags.every( t => selectedTagIds.has( t.id ) );
    }, [ filteredTags, selectedTagIds ] );

    // Toggle select all / deselect all downloads
    const handleToggleAllDownloads = useCallback( () => {
        if ( allFilteredDownloadsSelected ) {
            // Deselect all filtered downloads (pass filtered items for selective deselection)
            if ( onDeselectAll ) {
                onDeselectAll( filteredDownloads );
            }
        } else {
            // Select all filtered downloads (merge with existing selection)
            if ( onSelectAll ) {
                onSelectAll( filteredDownloads );
            }
        }
    }, [ allFilteredDownloadsSelected, filteredDownloads, onSelectAll, onDeselectAll ] );

    // Toggle select all / deselect all categories
    const handleToggleAllCategories = useCallback( () => {
        if ( allFilteredCategoriesSelected ) {
            // Deselect all filtered categories (pass filtered items for selective deselection)
            if ( onDeselectAllCategories ) {
                onDeselectAllCategories( filteredCategories );
            }
        } else {
            // Select all filtered categories (merge with existing selection)
            if ( onSelectAllCategories ) {
                onSelectAllCategories( filteredCategories );
            }
        }
    }, [ allFilteredCategoriesSelected, filteredCategories, onSelectAllCategories, onDeselectAllCategories ] );

    // Toggle select all / deselect all tags
    const handleToggleAllTags = useCallback( () => {
        if ( allFilteredTagsSelected ) {
            // Deselect all filtered tags (pass filtered items for selective deselection)
            if ( onDeselectAllTags ) {
                onDeselectAllTags( filteredTags );
            }
        } else {
            // Select all filtered tags (merge with existing selection)
            if ( onSelectAllTags ) {
                onSelectAllTags( filteredTags );
            }
        }
    }, [ allFilteredTagsSelected, filteredTags, onSelectAllTags, onDeselectAllTags ] );

    // Get total selection count
    const totalSelectionCount = selectedDownloads.length + selectedCategories.length + selectedTags.length;

    // Get search placeholder based on active tab
    const getSearchPlaceholder = () => {
        switch ( activeTab ) {
            case TAB_CATEGORIES:
                return __( 'Search Categories...', 'sb-customizer' );
            case TAB_TAGS:
                return __( 'Search Tags...', 'sb-customizer' );
            default:
                return __( 'Search a Download or paste a download URL', 'sb-customizer' );
        }
    };

    // Render tab buttons
    const renderTabs = () => {
        if ( !showTabs ) return null;

        return (
            <div className='sb-woo-selector-tabs' role='tablist' aria-label={ __( 'Selection type', 'sb-customizer' ) }>
                <button
                    role='tab'
                    aria-selected={ activeTab === TAB_DOWNLOADS }
                    aria-controls='sb-edd-panel-downloads'
                    id='sb-edd-tab-downloads'
                    className={ 'sb-woo-selector-tab' + ( activeTab === TAB_DOWNLOADS ? ' sb-active' : '' ) }
                    onClick={ () => { setActiveTab( TAB_DOWNLOADS ); setSearchQuery( '' ); } }
                >
                    { __( 'Downloads', 'sb-customizer' ) }
                </button>
                <button
                    role='tab'
                    aria-selected={ activeTab === TAB_CATEGORIES }
                    aria-controls='sb-edd-panel-categories'
                    id='sb-edd-tab-categories'
                    className={ 'sb-woo-selector-tab' + ( activeTab === TAB_CATEGORIES ? ' sb-active' : '' ) }
                    onClick={ () => { setActiveTab( TAB_CATEGORIES ); setSearchQuery( '' ); } }
                >
                    { __( 'Categories', 'sb-customizer' ) }
                </button>
                <button
                    role='tab'
                    aria-selected={ activeTab === TAB_TAGS }
                    aria-controls='sb-edd-panel-tags'
                    id='sb-edd-tab-tags'
                    className={ 'sb-woo-selector-tab' + ( activeTab === TAB_TAGS ? ' sb-active' : '' ) }
                    onClick={ () => { setActiveTab( TAB_TAGS ); setSearchQuery( '' ); } }
                >
                    { __( 'Tags', 'sb-customizer' ) }
                </button>
            </div>
        );
    };

    // Render category item
    const renderCategoryItem = ( category ) => (
        <div
            key={ `cat-${ category.id }` }
            className={ 'sb-woo-selector-item sb-woo-selector-category-item' + ( isCategorySelected( category.id ) ? ' sb-selected' : '' ) }
            onClick={ () => onCategoryToggle && onCategoryToggle( category ) }
        >
            <div
                className='sb-woo-selector-item-checkbox'
                onClick={ ( e ) => e.stopPropagation() }
            >
                <Checkbox
                    value={ isCategorySelected( category.id ) }
                    enabled={ true }
                    onChange={ () => onCategoryToggle && onCategoryToggle( category ) }
                />
            </div>
            <div className='sb-woo-selector-item-icon'>
                <FolderIcon />
            </div>
            <div className='sb-woo-selector-item-info'>
                <span className='sb-woo-selector-item-name'>{ category.name }</span>
                <span className='sb-woo-selector-item-meta'>
                    { category.download_count } { category.download_count === 1 ? __( 'Download', 'sb-customizer' ) : __( 'Downloads', 'sb-customizer' ) }
                    { ' • ' }
                    { category.review_count } { category.review_count === 1 ? __( 'Review', 'sb-customizer' ) : __( 'Reviews', 'sb-customizer' ) }
                </span>
            </div>
        </div>
    );

    // Render tag item
    const renderTagItem = ( tag ) => (
        <div
            key={ `tag-${ tag.id }` }
            className={ 'sb-woo-selector-item sb-woo-selector-tag-item' + ( isTagSelected( tag.id ) ? ' sb-selected' : '' ) }
            onClick={ () => onTagToggle && onTagToggle( tag ) }
        >
            <div
                className='sb-woo-selector-item-checkbox'
                onClick={ ( e ) => e.stopPropagation() }
            >
                <Checkbox
                    value={ isTagSelected( tag.id ) }
                    enabled={ true }
                    onChange={ () => onTagToggle && onTagToggle( tag ) }
                />
            </div>
            <div className='sb-woo-selector-item-icon'>
                <TagIcon />
            </div>
            <div className='sb-woo-selector-item-info'>
                <span className='sb-woo-selector-item-name'>{ tag.name }</span>
                <span className='sb-woo-selector-item-meta'>
                    { tag.download_count } { tag.download_count === 1 ? __( 'Download', 'sb-customizer' ) : __( 'Downloads', 'sb-customizer' ) }
                    { ' • ' }
                    { tag.review_count } { tag.review_count === 1 ? __( 'Review', 'sb-customizer' ) : __( 'Reviews', 'sb-customizer' ) }
                </span>
            </div>
        </div>
    );

    // Render skeleton loaders for downloads loading state
    const renderSkeletonLoaders = useCallback( ( count = 6 ) => {
        return Array.from( { length: count } ).map( ( _, index ) => (
            <div key={ `skeleton-${ index }` } className='sb-woo-selector-skeleton'>
                <div className='sb-woo-selector-skeleton-checkbox'></div>
                <div className='sb-woo-selector-skeleton-image'></div>
                <div className='sb-woo-selector-skeleton-info'>
                    <div className='sb-woo-selector-skeleton-name'></div>
                    <div className='sb-woo-selector-skeleton-meta'></div>
                </div>
                <div className='sb-woo-selector-skeleton-price'></div>
            </div>
        ) );
    }, [] );

    // Render skeleton loaders for categories/tags loading state
    const renderCategoryTagSkeletonLoaders = useCallback( ( count = 6 ) => {
        return Array.from( { length: count } ).map( ( _, index ) => (
            <div key={ `skeleton-${ index }` } className='sb-woo-selector-skeleton sb-woo-selector-skeleton-category'>
                <div className='sb-woo-selector-skeleton-checkbox'></div>
                <div className='sb-woo-selector-skeleton-icon'></div>
                <div className='sb-woo-selector-skeleton-info'>
                    <div className='sb-woo-selector-skeleton-name'></div>
                    <div className='sb-woo-selector-skeleton-meta'></div>
                </div>
            </div>
        ) );
    }, [] );

    // Render list content based on active tab
    const renderListContent = () => {
        // Downloads tab
        if ( activeTab === TAB_DOWNLOADS ) {
            // Show skeleton loaders for initial load, active search, or pending search
            // isSearchPending now covers: typing, clearing, AND debounce fired but search not yet triggered
            if ( isSearching || isLoading || isSearchPending ) {
                return renderSkeletonLoaders( 6 );
            }

            if ( filteredDownloads.length === 0 && searchQuery ) {
                return (
                    <div className='sb-woo-selector-empty'>
                        { isUrlSearch ? <LinkOffIcon /> : SbUtils.printIcon( 'search', false, false, 32 ) }
                        <div className='sb-woo-selector-empty-content'>
                            <span className='sb-woo-selector-empty-title'>
                                { isUrlSearch
                                    ? __( 'No downloads found with that URL', 'sb-customizer' )
                                    : __( 'No results found', 'sb-customizer' )
                                }
                            </span>
                            <span className='sb-woo-selector-empty-message'>
                                { isUrlSearch
                                    ? __( 'We couldn\'t find any download related to that url. Please double check it and try again.', 'sb-customizer' )
                                    : sprintf( __( 'We couldn\'t find any results for "%s". Please try a different term.', 'sb-customizer' ), searchQuery )
                                }
                            </span>
                        </div>
                    </div>
                );
            }

            return filteredDownloads.map( ( download ) => (
                <div
                    key={ download.id }
                    className={ 'sb-woo-selector-item' + ( isDownloadSelected( download.id ) ? ' sb-selected' : '' ) }
                    onClick={ () => onDownloadToggle( download ) }
                >
                    <div
                        className='sb-woo-selector-item-checkbox'
                        onClick={ ( e ) => e.stopPropagation() }
                    >
                        <Checkbox
                            value={ isDownloadSelected( download.id ) }
                            enabled={ true }
                            onChange={ () => onDownloadToggle( download ) }
                        />
                    </div>
                    <div className='sb-woo-selector-item-image'>
                        {
                            download.image
                                ? <img src={ download.image } alt={ download.name } />
                                : <div className='sb-woo-selector-item-placeholder'></div>
                        }
                    </div>
                    <div className='sb-woo-selector-item-info'>
                        <span className='sb-woo-selector-item-name'>{ download.name }</span>
                        <span className='sb-woo-selector-item-meta'>
                            { download.review_count } { download.review_count === 1 ? __( 'Review', 'sb-customizer' ) : __( 'Reviews', 'sb-customizer' ) }
                        </span>
                    </div>
                    <SafeHtml
                        className='sb-woo-selector-item-price'
                        html={ download.price_html }
                        as='div'
                    />
                </div>
            ));
        }

        // Categories tab
        if ( activeTab === TAB_CATEGORIES ) {
            if ( loadingCategories ) {
                return renderCategoryTagSkeletonLoaders( 6 );
            }

            if ( filteredCategories.length === 0 && searchQuery ) {
                return (
                    <div className='sb-woo-selector-empty'>
                        { SbUtils.printIcon( 'search', false, false, 32 ) }
                        <div className='sb-woo-selector-empty-content'>
                            <span className='sb-woo-selector-empty-title'>{ __( 'No categories found', 'sb-customizer' ) }</span>
                            <span className='sb-woo-selector-empty-message'>
                                { sprintf( __( 'We couldn\'t find any categories for "%s". Please try a different term.', 'sb-customizer' ), searchQuery ) }
                            </span>
                        </div>
                    </div>
                );
            }

            if ( filteredCategories.length === 0 ) {
                return (
                    <div className='sb-woo-selector-empty'>
                        <FolderIcon />
                        <div className='sb-woo-selector-empty-content'>
                            <span className='sb-woo-selector-empty-title'>{ __( 'No categories available', 'sb-customizer' ) }</span>
                            <span className='sb-woo-selector-empty-message'>
                                { __( 'No download categories found in your store.', 'sb-customizer' ) }
                            </span>
                        </div>
                    </div>
                );
            }

            return filteredCategories.map( renderCategoryItem );
        }

        // Tags tab
        if ( activeTab === TAB_TAGS ) {
            if ( loadingTags ) {
                return renderCategoryTagSkeletonLoaders( 6 );
            }

            if ( filteredTags.length === 0 && searchQuery ) {
                return (
                    <div className='sb-woo-selector-empty'>
                        { SbUtils.printIcon( 'search', false, false, 32 ) }
                        <div className='sb-woo-selector-empty-content'>
                            <span className='sb-woo-selector-empty-title'>{ __( 'No tags found', 'sb-customizer' ) }</span>
                            <span className='sb-woo-selector-empty-message'>
                                { sprintf( __( 'We couldn\'t find any tags for "%s". Please try a different term.', 'sb-customizer' ), searchQuery ) }
                            </span>
                        </div>
                    </div>
                );
            }

            if ( filteredTags.length === 0 ) {
                return (
                    <div className='sb-woo-selector-empty'>
                        <TagIcon />
                        <div className='sb-woo-selector-empty-content'>
                            <span className='sb-woo-selector-empty-title'>{ __( 'No tags available', 'sb-customizer' ) }</span>
                            <span className='sb-woo-selector-empty-message'>
                                { __( 'No download tags found in your store.', 'sb-customizer' ) }
                            </span>
                        </div>
                    </div>
                );
            }

            return filteredTags.map( renderTagItem );
        }

        return null;
    };

    // Render selected items in selection panel
    const renderSelectionItems = () => {
        if ( !showTabs ) {
            // Legacy mode - only downloads
            return selectedDownloads.map( ( download ) => (
                <div key={ download.id } className='sb-woo-selector-selected-item'>
                    <span className='sb-woo-selector-selected-name'>{ download.name }</span>
                    <button
                        className='sb-woo-selector-selected-remove'
                        onClick={ ( e ) => {
                            e.stopPropagation();
                            onDownloadToggle( download );
                        }}
                        aria-label={ sprintf( __( 'Remove %s', 'sb-customizer' ), download.name ) }
                    >
                        <CloseIcon />
                    </button>
                </div>
            ));
        }

        // Tabs mode - show mixed items with icons
        const items = [];

        // Add tags first
        selectedTags.forEach( tag => {
            items.push(
                <div key={ `tag-${ tag.id }` } className='sb-woo-selector-selected-item'>
                    <div className='sb-woo-selector-selected-icon sb-type-tag' aria-hidden='true'>
                        <TagIcon />
                    </div>
                    <span className='sb-woo-selector-selected-name'>{ tag.name }</span>
                    <button
                        className='sb-woo-selector-selected-remove'
                        onClick={ ( e ) => {
                            e.stopPropagation();
                            onTagToggle && onTagToggle( tag );
                        }}
                        aria-label={ sprintf( __( 'Remove %s', 'sb-customizer' ), tag.name ) }
                    >
                        <CloseIcon />
                    </button>
                </div>
            );
        });

        // Add categories
        selectedCategories.forEach( category => {
            items.push(
                <div key={ `cat-${ category.id }` } className='sb-woo-selector-selected-item'>
                    <div className='sb-woo-selector-selected-icon sb-type-category' aria-hidden='true'>
                        <FolderIcon />
                    </div>
                    <span className='sb-woo-selector-selected-name'>{ category.name }</span>
                    <button
                        className='sb-woo-selector-selected-remove'
                        onClick={ ( e ) => {
                            e.stopPropagation();
                            onCategoryToggle && onCategoryToggle( category );
                        }}
                        aria-label={ sprintf( __( 'Remove %s', 'sb-customizer' ), category.name ) }
                    >
                        <CloseIcon />
                    </button>
                </div>
            );
        });

        // Add downloads
        selectedDownloads.forEach( download => {
            items.push(
                <div key={ `dl-${ download.id }` } className='sb-woo-selector-selected-item'>
                    <div className='sb-woo-selector-selected-icon sb-type-product' aria-hidden='true'>
                        { download.image
                            ? <img src={ download.image } alt={ download.name } />
                            : <div className='sb-woo-selector-item-placeholder'></div>
                        }
                    </div>
                    <span className='sb-woo-selector-selected-name'>{ download.name }</span>
                    <button
                        className='sb-woo-selector-selected-remove'
                        onClick={ ( e ) => {
                            e.stopPropagation();
                            onDownloadToggle( download );
                        }}
                        aria-label={ sprintf( __( 'Remove %s', 'sb-customizer' ), download.name ) }
                    >
                        <CloseIcon />
                    </button>
                </div>
            );
        });

        return items;
    };

    // Handle clear all
    const handleClearAll = () => {
        if ( showTabs && onClearAll ) {
            onClearAll();
        } else if ( onDeselectAll ) {
            onDeselectAll();
        }
    };

    // Determine which header/footer to show based on active tab
    const showDownloadHeader = activeTab === TAB_DOWNLOADS;
    const showCategoryHeader = activeTab === TAB_CATEGORIES;
    const showTagHeader = activeTab === TAB_TAGS;

    return (
        <div className='sb-woo-selector-container'>
            {/* Two-panel layout */}
            <div className='sb-woo-selector-layout'>
                {/* Left Panel - Item List */}
                <div className='sb-woo-selector-products'>
                    {/* Tabs */}
                    { renderTabs() }

                    {/* Search Input */}
                    <div className='sb-woo-selector-search'>
                        <div className='sb-woo-selector-search-icon' aria-hidden='true'>
                            { SbUtils.printIcon( 'search', false, false, 16 ) }
                        </div>
                        <input
                            type='text'
                            className={ 'sb-woo-selector-search-input' }
                            placeholder={ getSearchPlaceholder() }
                            value={ searchQuery }
                            onChange={ ( e ) => setSearchQuery( e.target.value ) }
                            aria-label={ getSearchPlaceholder() }
                        />
                    </div>

                    {/* Item List Container */}
                    <div className='sb-woo-selector-list-container'>
                        {/* Header Row - Downloads Tab */}
                        { showDownloadHeader && (
                            <div className='sb-woo-selector-list-header'>
                                <div className='sb-woo-selector-header-checkbox'>
                                    <Checkbox
                                        value={ filteredDownloads.length > 0 ? allFilteredDownloadsSelected : true }
                                        enabled={ filteredDownloads.length > 0 }
                                        onChange={ handleToggleAllDownloads }
                                    />
                                </div>
                                <span className='sb-woo-selector-header-label'>{ __( 'Download', 'sb-customizer' ) }</span>
                                <span className='sb-woo-selector-header-price'>{ __( 'Price', 'sb-customizer' ) }</span>
                            </div>
                        )}

                        {/* Header Row - Categories Tab */}
                        { showCategoryHeader && (
                            <div className='sb-woo-selector-category-header'>
                                <div className='sb-woo-selector-header-checkbox'>
                                    <Checkbox
                                        value={ filteredCategories.length > 0 ? allFilteredCategoriesSelected : true }
                                        enabled={ filteredCategories.length > 0 }
                                        onChange={ handleToggleAllCategories }
                                    />
                                </div>
                                <span className='sb-woo-selector-header-label'>{ __( 'Categories', 'sb-customizer' ) }</span>
                            </div>
                        )}

                        {/* Header Row - Tags Tab */}
                        { showTagHeader && (
                            <div className='sb-woo-selector-tag-header'>
                                <div className='sb-woo-selector-header-checkbox'>
                                    <Checkbox
                                        value={ filteredTags.length > 0 ? allFilteredTagsSelected : true }
                                        enabled={ filteredTags.length > 0 }
                                        onChange={ handleToggleAllTags }
                                    />
                                </div>
                                <span className='sb-woo-selector-header-label'>{ __( 'Tags', 'sb-customizer' ) }</span>
                            </div>
                        )}

                        {/* Scrollable Item List */}
                        <div
                            className={ 'sb-woo-selector-list' + ( showTabs ? ' sb-with-tabs' : '' ) }
                            role={ showTabs ? 'tabpanel' : undefined }
                            id={ showTabs ? `sb-edd-panel-${ activeTab }` : undefined }
                            aria-labelledby={ showTabs ? `sb-edd-tab-${ activeTab }` : undefined }
                        >
                            { renderListContent() }
                        </div>

                        {/* Footer Row - Downloads Tab */}
                        { showDownloadHeader && (
                            <div className='sb-woo-selector-list-footer'>
                                <div className='sb-woo-selector-header-checkbox'>
                                    <Checkbox
                                        value={ filteredDownloads.length > 0 ? allFilteredDownloadsSelected : true }
                                        enabled={ filteredDownloads.length > 0 }
                                        onChange={ handleToggleAllDownloads }
                                    />
                                </div>
                                <span className='sb-woo-selector-header-label'>{ __( 'Download', 'sb-customizer' ) }</span>
                                <span className='sb-woo-selector-header-price'>{ __( 'Price', 'sb-customizer' ) }</span>
                            </div>
                        )}

                        {/* Footer Row - Categories Tab */}
                        { showCategoryHeader && (
                            <div className='sb-woo-selector-category-footer'>
                                <div className='sb-woo-selector-header-checkbox'>
                                    <Checkbox
                                        value={ filteredCategories.length > 0 ? allFilteredCategoriesSelected : true }
                                        enabled={ filteredCategories.length > 0 }
                                        onChange={ handleToggleAllCategories }
                                    />
                                </div>
                                <span className='sb-woo-selector-header-label'>{ __( 'Categories', 'sb-customizer' ) }</span>
                            </div>
                        )}

                        {/* Footer Row - Tags Tab */}
                        { showTagHeader && (
                            <div className='sb-woo-selector-tag-footer'>
                                <div className='sb-woo-selector-header-checkbox'>
                                    <Checkbox
                                        value={ filteredTags.length > 0 ? allFilteredTagsSelected : true }
                                        enabled={ filteredTags.length > 0 }
                                        onChange={ handleToggleAllTags }
                                    />
                                </div>
                                <span className='sb-woo-selector-header-label'>{ __( 'Tags', 'sb-customizer' ) }</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Panel - Selected Items */}
                <div className='sb-woo-selector-selected'>
                    {/* Selected Items Box */}
                    <div className={ 'sb-woo-selector-selected-box' + ( ( showTabs ? totalSelectionCount : selectedDownloads.length ) > 0 ? ' sb-has-items' : '' ) }>
                        {/* Header */}
                        <div className='sb-woo-selector-selected-header'>
                            <span className='sb-text-small sb-bold'>
                                { __( 'Your Selection', 'sb-customizer' ) }
                            </span>
                            {
                                ( showTabs ? totalSelectionCount : selectedDownloads.length ) > 0 &&
                                <button
                                    className='sb-woo-selector-clear-all'
                                    onClick={ handleClearAll }
                                >
                                    { __( 'Clear All', 'sb-customizer' ) }
                                </button>
                            }
                        </div>

                        {/* Validation Error Banner - Inline below header */}
                        {
                            validationError &&
                            <div className='sb-woo-selector-error-banner'>
                                { SbUtils.printIcon( 'warning', false, false, 16 ) }
                                <span className='sb-woo-selector-error-text'>{ validationError }</span>
                            </div>
                        }

                        {/* Selected List */}
                        <div className='sb-woo-selector-selected-list'>
                            {
                                ( showTabs ? totalSelectionCount : selectedDownloads.length ) === 0 &&
                                <div className='sb-woo-selector-selected-empty'>
                                    <HighlightMouseCursorIcon />
                                    <div className='sb-woo-selector-empty-text'>
                                        <span className='sb-woo-selector-empty-title'>
                                            { __( 'No downloads selected', 'sb-customizer' ) }
                                        </span>
                                        <span className='sb-woo-selector-empty-desc'>
                                            { __( 'As you select downloads, categories or tags, they will show up here', 'sb-customizer' ) }
                                        </span>
                                    </div>
                                </div>
                            }

                            { renderSelectionItems() }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EddDownloadSelector;
